import React, {
  createContext, ReactNode, useContext, useEffect, useMemo, useState,
} from 'react';
import { INVENTORY_TOKEN_KEY } from '../apollo-client';
import { useInventoryIdentifyMutation } from './graphql';

/** The lightweight borrower identity persisted for the trust-based flow. */
export interface Borrower {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
}

interface InventoryAuthContextType {
  borrower: Borrower | null;
  loading: boolean;
  error: string | null;
  identify: (name: string, email: string) => Promise<boolean>;
  signOut: () => void;
}

const BORROWER_KEY = 'inventoryBorrower';

const InventoryAuthContext = createContext<InventoryAuthContextType>(undefined);

const InventoryAuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [borrower, setBorrower] = useState<Borrower | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [identifyMutation, { loading }] = useInventoryIdentifyMutation();

  // Rehydrate a previously-identified borrower on load (kiosk refresh / deep link).
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(BORROWER_KEY);
      if (stored) setBorrower(JSON.parse(stored));
    } catch (e) {
      // ignore corrupt storage
    }
  }, []);

  const identify = async (name: string, email: string): Promise<boolean> => {
    setError(null);
    try {
      const { data } = await identifyMutation({ variables: { data: { name, email } } });
      const result = data?.inventoryIdentify;
      if (!result?.token || !result?.user) {
        setError(result?.errors?.[0]?.message || 'Could not identify you. Please try again.');
        return false;
      }
      window.localStorage.setItem(INVENTORY_TOKEN_KEY, result.token);
      window.localStorage.setItem(BORROWER_KEY, JSON.stringify(result.user));
      setBorrower(result.user);
      return true;
    } catch (e: any) {
      setError(e?.message || 'Could not identify you. Please try again.');
      return false;
    }
  };

  const signOut = () => {
    window.localStorage.removeItem(INVENTORY_TOKEN_KEY);
    window.localStorage.removeItem(BORROWER_KEY);
    setBorrower(null);
  };

  const value = useMemo(
    () => ({
      borrower, loading, error, identify, signOut,
    }),
    [borrower, loading, error],
  );

  return (
    <InventoryAuthContext.Provider value={value}>
      {children}
    </InventoryAuthContext.Provider>
  );
};

const useInventoryAuth = (): InventoryAuthContextType => {
  const ctx = useContext(InventoryAuthContext);
  if (!ctx) throw new Error('useInventoryAuth must be used within an InventoryAuthProvider');
  return ctx;
};

export { InventoryAuthProvider, InventoryAuthContext, useInventoryAuth };
